/**
 * 편성표 응답(raw) → 화면용 편성표 행 변환 (순수 함수)
 *
 * 각 슬롯의 run(startTime/endTime)으로 duration을 직접 역산하므로
 * 개별 job 조회가 필요 없다. useDashboard(오늘)와 useSchedule(임의 날짜)이 공유.
 */

import type { ScheduleResponse } from '../api/types';
import type { ScheduleRow, ScheduleSlot } from '../types';

export function buildScheduleRows(schedule: ScheduleResponse): ScheduleRow[] {
  const { nowHour } = schedule;
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

  // rows/slots가 배열이 아니면 빈 편성표로 방어(부분 응답 대비).
  const rows = Array.isArray(schedule?.rows) ? schedule.rows : [];
  return rows.map((row) => {
    const slots: ScheduleSlot[] = [];
    const rowSlots = Array.isArray(row.slots) ? row.slots : [];
    rowSlots.forEach((slot) => {
      const run = slot.run;
      if (run) {
        // 과거/진행 중: run으로 duration 막대. endTime이 없으면(RUNNING) 현재 시각을 종료점으로.
        const start = new Date(run.startTime);
        const finish = run.endTime ? new Date(run.endTime) : new Date();
        const durationMs = finish.getTime() - start.getTime();
        const durationHours = durationMs / 3_600_000;
        const durationMin = Math.round(durationMs / 60000);
        // 1분 미만이면 분 표기가 "0분"이 되므로 초 단위로 표시.
        const durationLabel =
          durationMin >= 1 ? `${durationMin}분` : `${Math.round(durationMs / 1000)}초`;
        // 실제 시작 시각의 하루 중 위치 (분 단위 소수 포함)
        const startHour = start.getHours() + start.getMinutes() / 60;
        const endLabel = run.endTime ? fmtTime(run.endTime) : '진행 중';
        // 수동 실행(trigger==='manual')이면 막대에 테두리를 줘 자동(cron) 실행과 구분.
        // trigger는 run 안이 아니라 slot 레벨(run의 형제)에 온다.
        const manual = slot.trigger === 'manual';
        const triggerLabel = manual ? ' · 수동' : '';
        const tooltip = `#${run.jobId} ${row.name} · ${run.status}${triggerLabel} · ${fmtTime(
          run.startTime
        )}~${endLabel} · ${durationLabel}`;
        // 백엔드 slot.state는 S/F/R/U만 준다. RETRY_SUCCESS는 run.status로만 오므로
        // 여기서 재시도 성공(Y)으로 승격해 편성표 막대도 success2로 구분되게 한다.
        const state = (run.status as string) === 'RETRY_SUCCESS' ? 'Y' : slot.state;
        slots.push({ hour: startHour, state, durationHours, tooltip, jobId: run.jobId, manual });
      } else if (slot.hour >= nowHour) {
        // 예정: 지금 이후 빈 슬롯만 점(10px)으로. durationHours/tooltip/jobId 없음.
        slots.push({ hour: slot.hour, state: slot.state });
      }
      // run 없고 과거인 슬롯(미실행/누락)은 렌더하지 않음
    });
    return { name: row.name, slots };
  });
}
